<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { caregiversApi } from '@/api/caregivers';
import {
  medicationsApi, type AdministrationForm, type AsNeededMedication, type DueDose, type StatusReason,
} from '@/api/medications';
import { apiErrorText } from '@/utils/api-errors';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// The office records a dose a caregiver reported: given or not (and why), when,
// who gave it and how much. As-needed and extra doses are always given.
const props = defineProps<{
  patientId: number;
  dose: DueDose | AsNeededMedication;
  mode: 'scheduled' | 'asNeeded' | 'extra';
}>();
const emit = defineEmits<{ close: [] }>();

const REASONS: { value: StatusReason; title: string }[] = [
  { value: 'refused', title: 'Rechazada por el paciente' },
  { value: 'held', title: 'Retenida (indicación clínica)' },
  { value: 'unavailable', title: 'No disponible' },
];

const queryClient = useQueryClient();
const given = ref(true);
const statusReason = ref<StatusReason>('refused');
const reason = ref('');
const givenAt = ref('');
const performerId = ref<number | undefined>();
const quantity = ref<number>(props.dose.doseQuantity);
const openedNewContainer = ref(false);
const notes = ref('');
const error = ref('');

const { data: caregivers } = useQuery({
  queryKey: ['caregiversForFilters'],
  queryFn: () => caregiversApi.indexForFilters(),
  initialData: [],
});

const scheduledAt = computed(() => ('scheduledAt' in props.dose ? props.dose.scheduledAt : undefined));
const title = computed(() => {
  if (props.mode === 'extra') return 'Registrar dosis extra';
  if (props.mode === 'asNeeded') return 'Registrar dosis SOS';

  return 'Registrar dosis';
});
const reasonRequired = computed(() => !given.value || props.mode === 'extra');
const valid = computed(() => (
  (!reasonRequired.value || reason.value.trim().length > 0) &&
  (!given.value || (!!performerId.value && quantity.value > 0))
));

function outcome(): Partial<AdministrationForm> {
  if (!given.value) return { status: 'not_done', statusReason: statusReason.value };

  return {
    status: 'completed', performerCaregiverId: performerId.value, quantity: quantity.value,
    openedNewContainer: openedNewContainer.value,
  };
}

// Empty given time: when it was due (scheduled doses) or now.
function timing(): Partial<AdministrationForm> {
  if (props.mode !== 'scheduled') return { givenAt: givenAt.value || undefined };

  return { scheduledAt: scheduledAt.value, givenAt: givenAt.value || scheduledAt.value };
}

const save = useMutation({
  mutationFn: () => medicationsApi.recordAdministration(props.patientId, {
    medicationDosageId: props.dose.dosageId,
    status: 'completed',
    ...outcome(),
    ...timing(),
    reason: reason.value || undefined,
    extraDose: props.mode === 'extra',
    notes: notes.value || undefined,
  }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['medicationDoses', props.patientId] });
    emit('close');
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo registrar la dosis.');
  },
});
</script>
<template>
  <responsive-dialog
    :model-value="true"
    :title="title"
    max-width="560"
    scrollable
    @close="emit('close')"
  >
    <div class="d-flex flex-column ga-4 pt-2">
      <p class="text-body-2">
        <b>{{ dose.drugName }}</b>
        <span class="d-block text-medium-emphasis">{{ dose.summary }}</span>
      </p>
      <v-btn-toggle
        v-if="mode === 'scheduled'"
        v-model="given"
        mandatory
        color="primary"
        variant="outlined"
        density="compact"
        divided
      >
        <v-btn :value="true">
          Administrada
        </v-btn>
        <v-btn :value="false">
          No administrada
        </v-btn>
      </v-btn-toggle>
      <v-select
        v-if="!given"
        v-model="statusReason"
        :items="REASONS"
        label="Por qué no se administró"
        variant="outlined"
        density="compact"
        hide-details
      />
      <v-text-field
        v-if="reasonRequired"
        v-model="reason"
        :label="mode === 'extra' ? 'Por qué se da una dosis extra' : 'Detalle'"
        variant="outlined"
        density="compact"
        hide-details
      />
      <template v-if="given">
        <v-autocomplete
          v-model="performerId"
          :items="caregivers"
          item-title="fullName"
          item-value="id"
          label="Profesional que la administró"
          variant="outlined"
          density="compact"
          hide-details
        />
        <div class="d-flex flex-wrap ga-3">
          <v-text-field
            v-model.number="quantity"
            type="number"
            min="0"
            step="0.25"
            label="Cantidad"
            :suffix="dose.doseUnitLabel"
            variant="outlined"
            density="compact"
            hide-details
            class="quantity-field"
          />
          <v-text-field
            v-model="givenAt"
            type="datetime-local"
            label="Hora en que se dio"
            :hint="mode === 'scheduled' ? 'Vacío: a la hora indicada' : 'Vacío: ahora'"
            persistent-hint
            variant="outlined"
            density="compact"
            class="flex-grow-1"
          />
        </div>
        <v-checkbox
          v-if="dose.consumptionPolicy === 'per_container'"
          v-model="openedNewContainer"
          label="Se abrió un envase nuevo (se descuenta del inventario)"
          density="compact"
          hide-details
        />
      </template>
      <v-text-field
        v-model="notes"
        label="Observaciones (opcional)"
        variant="outlined"
        density="compact"
        hide-details
      />
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        :text="error"
      />
    </div>
    <template #actions>
      <v-spacer />
      <v-btn
        :disabled="save.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="!valid"
        :loading="save.isPending.value"
        @click="save.mutate()"
      >
        Registrar
      </v-btn>
    </template>
  </responsive-dialog>
</template>
<style scoped>
.quantity-field {
  max-width: 200px;
}
</style>
